import { SortAscendingIcon, SortDescendingIcon } from '@heroicons/react/outline';
import clsx, { ClassValue } from 'clsx';
import Select from 'components/Select';
import TableExport from 'components/tables/TableExport';
import TableIndeterminateCheckbox from 'components/tables/TableIndeterminateCheckbox';
import TablePagination from 'components/tables/TablePagination';
import TableSearch from 'components/tables/TableSearch';
import { FC, Fragment, ReactNode, useEffect, useState } from 'react';
import {
  useExpanded,
  useFilters,
  useGlobalFilter,
  useRowSelect,
  useSortBy,
  useTable
} from 'react-table';
import { TABLE_PER_PAGE } from 'utils/constants';
import { ISelectOptions } from 'utils/models';
import TableDatePicker from './tables/TableDatePicker';
import TableSearchPatient from './tables/TableSearchPatient';

interface ITable {
  columns: any;
  data: any[];
  page?: number;
  totalCount?: number;
  onSetPage?: (page: number) => void;
  search?: string;
  onSearch?: (search: string) => void;
  isDateRange?: boolean;
  onChangeDate?: (date: any) => void;
  isSortable?: boolean;
  isExportable?: boolean;
  hasCheckbox?: boolean;
  perPage?: number;
  onPerPageSelect?: (perPage: number) => void;
  onRowSelect?: (details: any) => void; // check why any?
  actions?: ReactNode;
  isLoading?: boolean;
  setSort?: (sort: any) => void;
  tabs?: ReactNode;
  theme?: 'default' | 'version-1';
  isScrollable?: {
    maxHeight: ClassValue;
  };
  renderRowSubComponent?: any;
  expandOnRowClick?: boolean;
  isPatientSearch?: boolean;
  checkValue?: string[];
  setCheckValue?: (data: string[]) => void;
  handleSearchPatient?: () => void;
  setToggle?: any;
  toggle?: string;
  isToggle?: boolean;
  toggleOption?: ISelectOptions[];
  isToggleBrand?: boolean;
  isProductBrand?: ISelectOptions[];
  isProductBrandFetching?: boolean;
  setBrand?: any;
  isSortManual?: boolean;
  manualSorter?: ReactNode;
}

const colSpan: { [key: number]: string } = {
  1: 'col-span-1',
  2: 'col-span-2',
  3: 'col-span-3',
  4: 'col-span-4',
  5: 'col-span-5',
  6: 'col-span-6',
  7: 'col-span-7',
  8: 'col-span-8',
  9: 'col-span-9',
  10: 'col-span-10',
  11: 'col-span-11',
  12: 'col-span-12'
};

const themes = {
  default: {
    mainWrap: 'space-y-5 font-Sohne-Regular',
    header: 'text-sm bg-gray-300 bg-opacity-30 font-Sohne-Semibold',
    tableBorder: 'border-b',
    tableBg: '',
    tableBottom: '',
    tableLeft: '',
    tableRight: ''
  },
  'version-1': {
    mainWrap: '',
    header: 'bg-white text-xs border-b border-gray-100 font-semibold',
    tableBorder: 'border-b border-gray-100',
    tableBg: 'bg-white',
    tableBottom: '',
    tableLeft: '',
    tableRight: ''
  }
};

const Table: FC<ITable> = ({
  columns,
  data,
  page = 0,
  totalCount,
  onSetPage,
  isSortable = false,
  search,
  onSearch,
  isDateRange = false,
  onChangeDate,
  isExportable = false,
  hasCheckbox = false,
  perPage,
  onPerPageSelect,
  onRowSelect,
  actions,
  isLoading = false,
  setSort,
  tabs,
  theme = 'default',
  isScrollable,
  renderRowSubComponent,
  expandOnRowClick,
  isPatientSearch,
  setCheckValue,
  handleSearchPatient,
  setToggle,
  toggle,
  isToggle,
  toggleOption,
  isToggleBrand,
  isProductBrand,
  isProductBrandFetching,
  setBrand,
  isSortManual = false,
  manualSorter
}) => {
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    selectedFlatRows,
    headers
  } = useTable(
    {
      columns,
      data,
      manualPagination: true,
      manualSortBy: isSortManual,
      autoResetExpanded: false,
      getRowId: (row: any) => row.id ?? row._id ?? row.order_id
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    useExpanded,
    useRowSelect,
    (hooks) => {
      hasCheckbox &&
        hooks.visibleColumns.push((columns) => [
          {
            id: 'selection', // this is important for excluding when row is clicked
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <div>
                <TableIndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
              </div>
            ),
            Cell: ({ row }) => (
              <div>
                <TableIndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
              </div>
            )
          },
          ...columns
        ]);
    }
  );

  useEffect(() => {
    if (setSort && state.sortBy.length > 0) setSort(state.sortBy[0]);
  }, [setSort, state]);

  const tmplCols = headers.map((x: any) => x.Header === '' ? 'max-content' : 'minmax(0, auto)').join(' ');

  // Render the UI for your table
  return (
    <div className={clsx(themes[theme].mainWrap, isScrollable && 'h-full flex flex-col')}>
      <div className="flex justify-between shrink-0">
        {tabs}
        {manualSorter && manualSorter}
        {(onChangeDate || onPerPageSelect || onSetPage) && (
          <div className="flex justify-end items-center space-x-3">
            {onChangeDate && (
              <TableDatePicker
                onChange={onChangeDate}
                isDateRange={isDateRange}
                isLoading={isLoading}
              />
            )}

            {onPerPageSelect && onSetPage && (
              <div className="flex items-end space-x-2">
                <div className="w-60">
                  <Select
                    value={perPage}
                    className="text-xs "
                    // onChange={onPerPageSelect}
                    onChange={(perPage) => {
                      onPerPageSelect(perPage);
                      onSetPage(1);
                    }}
                    options={TABLE_PER_PAGE}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {isExportable && (
          <div className="flex items-center space-x-2 justify-between">
            {isExportable && <TableExport rowSelected={selectedFlatRows} />}
          </div>
        )}

        {(onSearch || actions) && (
          <div className={clsx('flex justify-end items-end space-x-3', !isPatientSearch && 'mr-0')}>
            {actions && <div className="flex justify-end items-end">{actions}</div>}

            {onSearch && !isPatientSearch && theme !== 'version-1' && (
              <TableSearch
                totalCount={totalCount || 0}
                search={search || ''}
                onSearch={onSearch}
                theme={theme}
              />
            )}
            {onSearch && isPatientSearch && (
              <TableSearchPatient
                search={search || ''}
                onSearch={onSearch}
                theme={theme}
                setCheckValue={setCheckValue}
                handleSearchPatient={handleSearchPatient}
              />
            )}
            {isToggleBrand && (
              <div className="w-72">
                <Select
                  className="text-xs"
                  value={toggle}
                  // onChange={onPerPageSelect}
                  onChange={(data: string) => {
                    setBrand(data);
                  }}
                  options={isProductBrand ? isProductBrand : []}
                  disabled={isProductBrandFetching}
                  placeholder="Brand"
                />
              </div>
            )}
            {isToggle && (
              <div className="w-64">
                <Select
                  className="text-xs"
                  value={toggle}
                  // onChange={onPerPageSelect}
                  onChange={(data: string) => {
                    setToggle(data);
                  }}
                  options={toggleOption ? toggleOption : []}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <div
        {...getTableProps()}
        className={clsx(
          'md:grid rounded-lg overflow-hidden bg-white',
          isScrollable && 'overflow-y-auto flex-1 content-start',
          isScrollable?.maxHeight
        )}
        style={{
          gridTemplateColumns: tmplCols,
          ...(isScrollable
            ? {
                minHeight: 0,
                gridAutoRows: 'max-content',
                alignContent: 'start'
              }
            : {})
        }}
      >
        {headerGroups.map((headerGroup) =>
          headerGroup.headers.map((column, index: number) => (
            <div
              className={clsx(
                (column as any).compact ? 'px-2 py-3' : 'px-5 py-3',
                'text-left text-xs',
                theme === 'version-1' ? '' : 'bg-hazel-yellow-200',
                themes[theme].header,
                isScrollable && 'sticky top-0 z-10',
                'hidden md:block space-y-1'
              )}
              style={
                isScrollable && theme !== 'version-1' ? { backgroundColor: '#F7F5ED' } : undefined
              }
              key={index}
            >
              {isSortable ? (
                <div {...column.getHeaderProps(column.getSortByToggleProps())}>
                  <div className="flex items-center justify-between">
                    <span>{column.render('Header')}</span>
                    <span>
                      {column.isSorted ? (
                        column.isSortedDesc ? (
                          <SortDescendingIcon className="w-5 h-5 text-gray-500" />
                        ) : (
                          <SortAscendingIcon className="w-5 h-5 text-gray-500" />
                        )
                      ) : (
                        ''
                      )}
                    </span>
                  </div>
                </div>
              ) : (
                <div {...column.getHeaderProps()}>{column.render('Header')}</div>
              )}

              <div>{column.Filter && column.canFilter ? column.render('Filter') : null}</div>
            </div>
          ))
        )}

        <Fragment {...getTableBodyProps}>
          {rows.map((row, i) => {
            prepareRow(row);

            return (
              <Fragment key={`${i}-row`}>
                <div
                  {...row.getRowProps()}
                  className={clsx(
                    onRowSelect && 'cursor-pointer',
                    clsx(
                      // @ts-ignore
                      [false].includes(row.original?.is_active) ||
                        // @ts-ignore
                        [false].includes(row.original?.is_enabled) ||
                        // @ts-ignore
                        (row.original.status === 'INACTIVE' && 'bg-gray-50 text-gray-400'),
                      'my-6 mx-5 md:my-0 md:mx-0 md:contents'
                    )
                  )}
                  onMouseEnter={() => setHoveredRow(i)}
                  onMouseLeave={() => setHoveredRow(null)}
                >
                  {row.cells.map((cell, j) => {
                    return (
                      <div
                        {...cell.getCellProps()}
                        className={clsx(
                          i !== rows.length - 1 && themes[theme].tableBorder,
                          (cell.column as any).compact ? 'py-2 px-1 md:py-3 md:px-2' : 'py-2 px-4 md:p-4',
                          'border-gray-300 text-xs ',
                          'flex items-center',
                          hoveredRow === i && theme === 'version-1'
                            ? 'bg-[#FFFEF0]'
                            : themes[theme].tableBg,
                          themes[theme].tableBottom,
                          j === 0 && themes[theme].tableLeft,
                          j === row.cells.length - 1 && themes[theme].tableRight
                        )}
                        onClick={(event) => {
                          if (event.target === event.currentTarget) {
                            if (expandOnRowClick) {
                              row.toggleRowExpanded();
                            } else {
                              onRowSelect &&
                                cell.column.id !== 'selection' &&
                                onRowSelect(row.original);
                            }
                          }
                        }}
                      >
                        {cell.render('Cell') || '-'}
                      </div>
                    );
                  })}
                </div>

                {/**
                 * EXPANDED
                 */}
                {row.isExpanded ? (
                  <div className="my-6 mx-5 md:my-0 md:mx-0 md:contents md:group">
                    {renderRowSubComponent({ row })}
                  </div>
                ) : null}
              </Fragment>
            );
          })}
        </Fragment>

        {rows.length === 0 && (
          <div className="contents group">
            <div className={clsx(colSpan[headers.length], 'py-8 px-6 text-sm bg-white')}>
              <div className="flex justify-center text-md">
                {isLoading ? 'Loading...' : 'Nothing to Display'}
              </div>
            </div>
          </div>
        )}
      </div>

      {onSetPage && perPage && (
        <div className={clsx('flex justify-start my-5', isScrollable && 'shrink-0')}>
          <TablePagination
            totalRows={totalCount || 0}
            pageChangeHandler={onSetPage}
            rowsPerPage={perPage}
            page={page}
          />
        </div>
      )}
    </div>
  );
};

export default Table;
